import { useLoading } from '@/providers/LoadingProvider';
import { useBackofficeSearchCondition } from '@/providers/BackofficeSearchConditionProvider';
import { InvoiceTask, InvoiceTaskPhase } from '@/hooks/api/models';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { useInvoiceTaskPhases } from '@/hooks/api/1790_invoice_task_phases';
import { useInvoiceTasks } from '@/hooks/api/1800_invoice_tasks';
import { useCurrentWorkspace } from '@/providers/WorkspaceProvider';
import { useMoveInvoiceTaskPhaseToNext } from '@/hooks/api/1850_move_to_next_phase';
import { useMoveInvoiceTaskPhaseToPrev } from '@/hooks/api/1851_move_to_prev_phase';
import { useInvoiceTaskInvalidate } from '@/hooks/api/1840_invoice_task';
import { toastApiError } from '@/lib/toast';
import { Link, useNavigate } from 'react-router-dom';
import { dateFormat } from '@/utils';

type PhaseTasks = {
  phase: InvoiceTaskPhase;
  tasks: InvoiceTask[];
};

export const KanbanElement = () => {
  const { setShowLoading } = useLoading();
  const { invoiceTaskSearchCondition: searchCondition } = useBackofficeSearchCondition();
  const { data: phases } = useInvoiceTaskPhases();
  const {
    data: tasksRes,
    isPending,
    refetch,
  } = useInvoiceTasks({ ...searchCondition, invoice_task_phase_ids: [], per: 1000 });
  const [phaseTasksList, setPhaseTasksList] = useState<PhaseTasks[]>([]);
  const [updatedTask, setUpdatedTask] = useState<InvoiceTask>();

  useEffect(() => setShowLoading(isPending), [isPending]);

  useEffect(() => {
    refetch();
  }, [searchCondition]);

  useEffect(() => {
    if (updatedTask) {
      const _phaseTasksList: PhaseTasks[] = [...phaseTasksList];
      for (const _phaseTask of _phaseTasksList) {
        _phaseTask.tasks = _phaseTask.tasks.filter((d) => d.id != updatedTask.id);

        if (_phaseTask.phase.id == updatedTask.invoice_task_phase.id) {
          _phaseTask.tasks = [..._phaseTask.tasks, updatedTask];
        }
      }
      setPhaseTasksList((_) => _phaseTasksList);
      refetch();
    }
  }, [updatedTask]);

  useEffect(() => {
    if (phases && tasksRes) {
      const _phaseTasksList: PhaseTasks[] = [];
      for (const phase of phases) {
        const tasks = tasksRes.invoice_tasks.filter((d) => d.invoice_task_phase.id == phase.id);
        _phaseTasksList.push({ phase, tasks });
      }
      setPhaseTasksList((_) => _phaseTasksList);
    }
  }, [phases, tasksRes]);

  if (phases == null || tasksRes == null) return <></>;

  return (
    <div className="mb-4 kanban-container">
      <div className="py-2 d-inline-flex" style={{ overflowX: 'auto' }}>
        <div id="myKanban">
          <div className="kanban-container" style={{ width: 1550 }}>
            {phaseTasksList.map((d) => (
              <PhaseTasksElement
                key={`p-t-${d.phase.id}`}
                phases={phases}
                phaseTasks={d}
                setUpdatedTask={setUpdatedTask}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

type PhaseTasksProps = {
  phases: InvoiceTaskPhase[];
  phaseTasks: PhaseTasks;
  setUpdatedTask: Dispatch<SetStateAction<InvoiceTask | undefined>>;
};
const PhaseTasksElement = ({ phases, phaseTasks, setUpdatedTask }: PhaseTasksProps) => {
  return (
    <div
      data-id="_backlog"
      data-order="1"
      className="kanban-board"
      style={{ width: 300, marginLeft: 10, marginTop: 10 }}
    >
      <header className="kanban-board-header">
        <div className="kanban-title-board">{phaseTasks.phase.label}</div>
      </header>
      <main className="kanban-drag">
        {phaseTasks.tasks.map((d) => (
          <TaskElement key={`t-${d.id}`} phases={phases} task={d} setUpdatedTask={setUpdatedTask} />
        ))}
      </main>
      <footer></footer>
    </div>
  );
};

type TaskProps = {
  phases: InvoiceTaskPhase[];
  task: InvoiceTask;
  setUpdatedTask: Dispatch<SetStateAction<InvoiceTask | undefined>>;
};
const TaskElement = ({ phases, task, setUpdatedTask }: TaskProps) => {
  const navigate = useNavigate();
  const { setShowLoading } = useLoading();
  const { _w_ } = useCurrentWorkspace();
  const {
    mutate: mutateForNext,
    data: dataForNext,
    isPending: isPendingForNext,
    error: errorForNext,
  } = useMoveInvoiceTaskPhaseToNext();
  const {
    mutate: mutateForPrev,
    data: dataForPrev,
    isPending: isPendingForPrev,
    error: errorForPrev,
  } = useMoveInvoiceTaskPhaseToPrev();
  const { invalidate } = useInvoiceTaskInvalidate({
    identifier: _w_!.identifier,
    team_id: task.sales_order.team_outline.id,
    sales_order_id: task.sales_order.id,
    id: task.id,
  });

  useEffect(() => setShowLoading(isPendingForNext), [isPendingForNext]);
  useEffect(() => setShowLoading(isPendingForPrev), [isPendingForPrev]);
  useEffect(() => toastApiError(errorForNext), [errorForNext]);
  useEffect(() => toastApiError(errorForPrev), [errorForPrev]);

  useEffect(() => {
    if (dataForNext) {
      setUpdatedTask(dataForNext);
      invalidate();
    }
  }, [dataForNext]);

  useEffect(() => {
    if (dataForPrev) {
      setUpdatedTask(dataForPrev);
      invalidate();
    }
  }, [dataForPrev]);

  const onMoveNextClick = (e: React.MouseEvent) => {
    e.stopPropagation();

    setShowLoading(true);
    mutateForNext({
      identifier: _w_!.identifier,
      team_id: task.sales_order.team_outline.id,
      sales_order_id: task.sales_order.id,
      task_id: task.id,
      current_invoice_task_phase_id: task.invoice_task_phase.id,
    });
  };

  const onMovePrevClick = (e: React.MouseEvent) => {
    e.stopPropagation();

    setShowLoading(true);
    mutateForPrev({
      identifier: _w_!.identifier,
      team_id: task.sales_order.team_outline.id,
      sales_order_id: task.sales_order.id,
      task_id: task.id,
      current_invoice_task_phase_id: task.invoice_task_phase.id,
    });
  };

  return (
    <div
      className="kanban-item border-radius-md"
      onClick={() =>
        navigate(
          `/w/${_w_!.identifier}/backoffice/teams/${task.sales_order.team_outline.id}/sales_orders/${
            task.sales_order.id
          }/invoice_tasks/${task.id}`
        )
      }
    >
      {task.alert && (
        <div className="mb-2">
          <span className="badge badge-xs badge-danger">{task.alert}</span>
        </div>
      )}

      <div className="d-flex align-items-center">
        <h6 className="text-truncate mb-0 flex-1 text-sm me-1">{task.name}</h6>

        {phases[0].id != task.invoice_task_phase.id && (
          <div className="d-flex">
            <button className="btn btn-icon-only btn-sm bg-gradient-warning me-1" onClick={onMovePrevClick}>
              <i className="fas fa-arrow-left" aria-hidden="true"></i>
            </button>
          </div>
        )}
        {phases.at(-1)!.id != task.invoice_task_phase.id && (
          <button className="btn btn-icon-only btn-sm bg-gradient-success" onClick={onMoveNextClick}>
            <i className="fas fa-arrow-right" aria-hidden="true"></i>
          </button>
        )}
      </div>

      <div className="avatar-group mb-2">
        <img src={task.user.user_account.icon_file_url} className="avatar avatar-xs rounded-circle me-1" />
        <span className="text-xs text-dark text-truncate">{task.user.user_account.name}</span>
        {/*{task.invoice_task_subscriptions.map((d) => (*/}
        {/*  <img key={`t-subsctiption-${d.id}`} src={d.user.user_account.icon_file_url} className="avatar avatar-xs rounded-circle me-1" />*/}
        {/*))}*/}
      </div>
      <div className="d-flex flex-wrap">
        <span className="badge badge-xs bg-gradient-warning me-1 mb-1 text-truncate">
          {task.billing_schedule_detail.billing_schedule_outline.year_month}
        </span>
        <span className="badge badge-xs bg-gradient-info me-1 mb-1 text-truncate">{task.sales_order.name}</span>
        <span className="badge badge-xs bg-gradient-primary me-1 mb-1 text-truncate">
          {task.sales_order.business_partner.display_name}
        </span>
        <span className="badge badge-xs bg-gradient-dark me-1 mb-1 text-truncate">
          {task.sales_order.team_outline.name}
        </span>
        <span className="badge badge-xs bg-gradient-secondary me-1 mb-1 text-truncate">
          {task.sales_order.product.name}
        </span>
        {task.billing_amount_including_tax ? (
          <span className="badge badge-xs bg-gradient-success me-1 mb-1 text-truncate">
            ¥{task.billing_amount_including_tax.toLocaleString()}
          </span>
        ) : (
          <></>
        )}
      </div>
      <hr className="horizontal dark my-2" />
      <div className="d-flex justify-content-between">
        <div>
          <div className="text-secondary text-xxs">期限日</div>
          <div className="font-weight-bold text-dark text-xxs">
            {task.due_date ? dateFormat({ date: task.due_date }) : '-'}
          </div>
        </div>
        <div className="text-end">
          {/*<div className="text-secondary text-xxs">Assigned To</div>*/}
          {/*<div className="font-weight-bold text-dark text-xxs">{task.user.user_account.name}</div>*/}
        </div>
      </div>
    </div>
  );
};
